import { useEffect, useState } from 'react';
import { LAYOUT } from '../styles/tokens';

const MOBILE_QUERY = `(max-width: ${LAYOUT.mobileBreakpoint}px)`;

export interface Viewport {
  /** True when the viewport is at or below the mobile breakpoint. */
  isMobile: boolean;
}

/**
 * Tracks whether the hub is in the mobile layout band. Uses matchMedia so
 * resizes (and device rotation) update without a full remount.
 */
export function useViewport(): Viewport {
  const [isMobile, setIsMobile] = useState(() => readIsMobile());

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    function sync(): void {
      setIsMobile(media.matches);
    }
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return { isMobile };
}

/** Read the current mobile match. Safe during SSR-less Vite startup. */
function readIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * Open/close state for the mobile nav drawer. Closes on route change and
 * when the user presses Escape.
 */
export function useMobileNav(pathname: string): {
  menuOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
} {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return {
    menuOpen,
    toggleMenu: () => setMenuOpen(open => !open),
    closeMenu: () => setMenuOpen(false),
  };
}
